import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { schedulesVerificationService } from '@/services/schedulesVerification.service';
import { isSoftVerificationFailure } from '@/utils/scheduleVerification';
import {
  clearVerificationSession,
  countInProgress,
  countResolved,
  formatLoraVerifyEta,
  getVerificationSession,
  isTransitionalSummary,
  LORA_VERIFY_POLL_MS,
  startVerificationSession,
  updateVerificationSessionProgress,
} from '@/utils/verificationSession';
import type { ScheduleVerificationResponse } from '@/types/api';

export type ScheduleVerificationAvailability =
  | { status: 'ready'; data: ScheduleVerificationResponse }
  | { status: 'unavailable'; message: string }
  | null;

export interface UseScheduleVerificationOptions {
  /** Milesight/LoRa: cola secuencial; SWATID: false */
  isLora?: boolean;
  controllerName?: string;
  enabled?: boolean;
}

export function useScheduleVerification(
  controllerId: string,
  options: boolean | UseScheduleVerificationOptions = true
) {
  const opts: UseScheduleVerificationOptions =
    typeof options === 'boolean' ? { enabled: options } : options;
  const enabled = opts.enabled ?? true;
  const isLora = opts.isLora ?? true;
  const controllerName = opts.controllerName;

  const queryClient = useQueryClient();
  const [awaitingDevice, setAwaitingDevice] = useState(() =>
    Boolean(controllerId && getVerificationSession(controllerId))
  );
  const lastResolvedRef = useRef<number | null>(null);

  const statusQuery = useQuery({
    queryKey: ['schedule-verification', controllerId],
    queryFn: async (): Promise<ScheduleVerificationAvailability> => {
      const res = await schedulesVerificationService.getStatus(controllerId);
      if (!res.success || !res.data) {
        const code = res.error?.code;
        const message =
          res.error?.message || 'Verificación no disponible en este servidor';
        if (isSoftVerificationFailure(code, message)) {
          return { status: 'unavailable', message };
        }
        throw new Error(message);
      }
      return { status: 'ready', data: res.data };
    },
    enabled: !!controllerId && enabled,
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const value = query.state.data;
      if (!value || value.status !== 'ready') {
        return getVerificationSession(controllerId) ? LORA_VERIFY_POLL_MS : false;
      }
      if (
        isTransitionalSummary(value.data.summary) ||
        getVerificationSession(controllerId)
      ) {
        return LORA_VERIFY_POLL_MS;
      }
      return false;
    },
  });

  // Reanudar espera si hay sesión persistida o el backend sigue en progreso
  useEffect(() => {
    if (!controllerId || !enabled) return;
    const session = getVerificationSession(controllerId);
    const ready =
      statusQuery.data?.status === 'ready' ? statusQuery.data.data : null;
    if (session || (ready && isTransitionalSummary(ready.summary))) {
      setAwaitingDevice(true);
    }
  }, [controllerId, enabled, statusQuery.data]);

  // Progreso: actualizar sesión, limpiar al completar, notificar
  useEffect(() => {
    if (!controllerId) return;
    const value = statusQuery.data;
    if (!value || value.status !== 'ready') return;

    const { summary } = value.data;
    const resolved = countResolved(summary);
    const pending = countInProgress(summary);

    if (getVerificationSession(controllerId) || awaitingDevice) {
      updateVerificationSessionProgress(controllerId, summary);
    }

    if (pending === 0 && (awaitingDevice || getVerificationSession(controllerId))) {
      clearVerificationSession(controllerId);
      setAwaitingDevice(false);
    } else if (pending > 0) {
      setAwaitingDevice(true);
    }

    lastResolvedRef.current = resolved;
  }, [controllerId, statusQuery.data, statusQuery.dataUpdatedAt, awaitingDevice]);

  const verifyMutation = useMutation({
    mutationFn: async (force = true) => {
      const res = await schedulesVerificationService.verify(controllerId, force);
      if (!res.success || !res.data) {
        const code = res.error?.code;
        const message = res.error?.message || 'Error al verificar programaciones';
        if (isSoftVerificationFailure(code, message)) {
          throw new Error(
            'La verificación no está disponible en este servidor. Compruebe que el backend v0.7 esté desplegado.'
          );
        }
        throw new Error(message);
      }
      return res.data;
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(['schedule-verification', controllerId], {
        status: 'ready',
        data,
      } satisfies NonNullable<ScheduleVerificationAvailability>);

      if (isTransitionalSummary(data.summary)) {
        startVerificationSession({
          controllerId,
          controllerName: controllerName || data.controllerName,
          total: data.summary.total || data.schedules.length || 1,
          summary: data.summary,
          isLora,
        });
        setAwaitingDevice(true);
      } else {
        clearVerificationSession(controllerId);
        setAwaitingDevice(false);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['local-schedules', controllerId] }),
        queryClient.invalidateQueries({ queryKey: ['swatid-schedules', controllerId] }),
      ]);

      // Primer refresco diferido (no inmediato, evita pisar con GET stale)
      window.setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: ['schedule-verification', controllerId],
        });
      }, isLora ? 12_000 : 2_000);
    },
  });

  const refresh = useCallback(async () => {
    await statusQuery.refetch();
  }, [statusQuery]);

  const verify = useCallback(
    async (force = true) => {
      return verifyMutation.mutateAsync(force);
    },
    [verifyMutation]
  );

  const availability = statusQuery.data ?? null;
  const verificationStatus =
    availability?.status === 'ready' ? availability.data : null;
  const unavailable =
    availability?.status === 'unavailable' ? availability.message : null;

  const hardError =
    statusQuery.error instanceof Error
      ? statusQuery.error.message
      : verifyMutation.error instanceof Error
        ? verifyMutation.error.message
        : null;

  const summary = verificationStatus?.summary;
  const progress = summary
    ? {
        total: summary.total,
        resolved: countResolved(summary),
        verified: summary.verified,
        pending: countInProgress(summary),
        etaLabel:
          isLora && countInProgress(summary) > 0
            ? formatLoraVerifyEta(Math.max(1, countInProgress(summary)))
            : null,
      }
    : null;

  return {
    verificationStatus,
    unavailable,
    isLoading: statusQuery.isLoading,
    isVerifying: verifyMutation.isPending,
    awaitingDevice,
    progress,
    isLora,
    error: unavailable ? null : hardError,
    verify,
    refresh,
  };
}

/** Mensaje de toast tras POST verify según conteo LoRa */
export function buildVerifyToast(data: ScheduleVerificationResponse, isLora: boolean) {
  const issues =
    (data.summary.mismatch ?? 0) +
    (data.summary.missing ?? 0) +
    (data.summary.error ?? 0);
  const pending = countInProgress(data.summary);
  const total = data.summary.total || data.schedules.length || 0;

  if (issues > 0 && pending === 0) {
    return {
      title: 'Verificación con incidencias',
      description: `${issues} programación(es) no coinciden o faltan en el dispositivo.`,
      variant: 'destructive' as const,
    };
  }
  if (pending > 0) {
    const eta = isLora ? formatLoraVerifyEta(Math.max(pending, total > 1 ? total : pending)) : null;
    return {
      title:
        total > 1
          ? `Verificación iniciada (${total} programaciones)`
          : 'Verificación solicitada',
      description: isLora
        ? `LoRa verifica un slot por uplink. ETA ${eta}. Puede cerrar esta ventana; al volver verá el progreso. Le avisaremos si hay actualizaciones.`
        : 'Verificación en curso. Puede cerrar y volver más tarde.',
      variant: 'default' as const,
    };
  }
  return {
    title: 'Verificación completada',
    description: 'Las programaciones coinciden con el dispositivo.',
    variant: 'default' as const,
  };
}
